import React, { useState } from 'react';
import { Trash2, RotateCcw } from 'lucide-react';
import { AccessorialDescInput } from './AccessorialDescInput';

const money = (v) => {
  const n = parseFloat(v) || 0;
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

// Currency input: shows $1,234.56 when idle, raw number while editing; rounds to cents on blur
const MoneyInput = ({ value, onChange, readOnly, testId, dataField, onFocusSelect, onMouseUp }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const num = parseFloat(value);
  const hasValue = value !== '' && value !== null && value !== undefined && !isNaN(num);
  const emit = (v) => onChange({ target: { value: v } });
  return (
    <input
      type="text"
      inputMode="decimal"
      className="lw-input money"
      data-field={dataField}
      data-testid={testId}
      readOnly={readOnly}
      value={editing ? draft : (hasValue ? money(num) : '')}
      placeholder="$0.00"
      onFocus={(e) => {
        if (readOnly) return;
        setDraft(hasValue ? String(Math.round(num * 100) / 100) : '');
        setEditing(true);
        onFocusSelect && onFocusSelect(e);
      }}
      onMouseUp={onMouseUp}
      onChange={(e) => {
        const raw = e.target.value.replace(/[^0-9.-]/g, '');
        setDraft(raw);
        emit(raw);
      }}
      onBlur={() => {
        setEditing(false);
        const n = parseFloat(draft);
        emit(isNaN(n) ? '' : Math.round(n * 100) / 100);
      }}
    />
  );
};

// Financials: Line Haul is the only default row. Fuel (FSC) row appears when enabled.
// All other accessorials are user-added rows with code autocomplete + delete.
export const FinancialsPanel = ({ load, accessorials, onChange, onUpdateAccessorial, onAddAccessorial, onRemoveAccessorial, canOverrideFsc = false, onRemoveFsc, onRecalcFsc }) => {
  const isPerMile = load.line_haul_type === 'rpm';
  const carrierLH = parseFloat(load.carrier_line_haul) || 0;
  const customerLH = parseFloat(load.customer_line_haul) || 0;
  const fsc = load.fsc_enabled ? (parseFloat(load.fsc_amount) || 0) : 0;

  const accCarrier = accessorials.reduce((s, a) => s + (parseFloat(a.carrier_amount) || 0), 0);
  const accCustomer = accessorials.reduce((s, a) => s + (parseFloat(a.customer_amount) || 0), 0);

  const carrierTotal = carrierLH + accCarrier;
  const customerTotal = customerLH + accCustomer + fsc;
  const net = customerTotal - carrierTotal;
  const profitPct = customerTotal > 0 ? (net / customerTotal * 100) : 0;

  const selectOnFocus = (e) => {
    e.target.select();
    e.target.dataset.justFocused = '1';
  };
  const keepSelection = (e) => {
    // Prevent the mouseup right after focus from collapsing the selection
    if (e.target.dataset.justFocused === '1') {
      e.preventDefault();
      delete e.target.dataset.justFocused;
    }
  };

  const numInput = (value, onChangeFn, testId, readOnly = false) => (
    <MoneyInput
      value={value}
      onChange={onChangeFn}
      readOnly={readOnly}
      testId={testId}
      dataField={{ 'lw-fin-carrier-lh': 'carrier_line_haul', 'lw-fin-customer-lh': 'customer_line_haul', 'lw-fin-customer-fuel': 'fsc_amount' }[testId]}
      onFocusSelect={selectOnFocus}
      onMouseUp={keepSelection}
    />
  );

  return (
    <div className="lw-panel" data-testid="lw-panel-financials">
      <div className="lw-row" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
        <span className="lw-panel-title">Financials</span>
        <div className="lw-row" style={{ marginBottom: 0 }}>
          <span className="lw-label">Rate type</span>
          <select
            className="lw-input"
            style={{ maxWidth: 110 }}
            value={load.line_haul_type === 'rpm' ? 'Per Mile' : 'Flat Rate'}
            onChange={(e) => onChange('line_haul_type', e.target.value === 'Per Mile' ? 'rpm' : 'flat')}
            data-testid="lw-rate-type"
          >
            <option>Flat Rate</option>
            <option>Per Mile</option>
          </select>
          {isPerMile && (
            <>
              <span className="lw-label" style={{ marginLeft: 8 }} title="Line Haul per Mile">LHM</span>
              <span style={{ width: 90, display: 'inline-block' }}>
                <MoneyInput value={load.customer_rpm} onChange={(e) => onChange('customer_rpm', e.target.value)} testId="lw-fin-lhm" dataField="customer_rpm" onFocusSelect={selectOnFocus} onMouseUp={keepSelection} />
              </span>
            </>
          )}
          <span className="lw-link" onClick={onAddAccessorial} style={{ marginLeft: 8 }} data-testid="lw-add-accessorial">Add +</span>
        </div>
      </div>

      <table className="lw-fin-table" style={{ tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: '32%' }} />
          <col style={{ width: '30%' }} />
          <col style={{ width: '30%' }} />
          <col style={{ width: 26 }} />
        </colgroup>
        <thead>
          <tr><th></th><th>Carrier</th><th>Customer</th><th></th></tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ whiteSpace: 'nowrap' }}>
              Line Haul
              {isPerMile && <span style={{ fontSize: 11, color: 'var(--lw-muted)', marginLeft: 4 }} data-testid="lw-fin-lh-formula">(LHM × {parseFloat(load.customer_mileage) || 0} mi)</span>}
            </td>
            <td>{numInput(load.carrier_line_haul, (e) => onChange('carrier_line_haul', e.target.value), 'lw-fin-carrier-lh')}</td>
            <td>{numInput(load.customer_line_haul, (e) => onChange('customer_line_haul', e.target.value), 'lw-fin-customer-lh', isPerMile)}</td>
            <td></td>
          </tr>
          {load.fsc_enabled && (
            <tr data-testid="lw-fin-fuel-row">
              <td style={{ whiteSpace: 'nowrap' }}>
                Fuel{' '}
                {load.fsc_fuel_price ? (
                  <span style={{ fontSize: 11, color: 'var(--lw-muted)' }} data-testid="lw-fin-fuel-reference" title={`EIA diesel ${load.fsc_price_date || ''}`}>
                    (${Number(load.fsc_fuel_price).toFixed(2)}
                    {load.fsc_rpm !== null && load.fsc_rpm !== undefined ? ` → $${Number(load.fsc_rpm).toFixed(3)}` : load.fsc_percentage !== null && load.fsc_percentage !== undefined ? ` → ${Number(load.fsc_percentage).toFixed(2)}%` : ''})
                  </span>
                ) : load.fsc_percentage ? (
                  <span style={{ fontSize: 11, color: 'var(--lw-muted)' }}>({load.fsc_percentage}%)</span>
                ) : null}
                {load.fsc_override && <span style={{ fontSize: 10, marginLeft: 4, padding: '1px 5px', borderRadius: 3, background: '#ffe8d1', color: '#7a3b00', fontWeight: 700 }} data-testid="lw-fin-fuel-override-tag">manual</span>}
              </td>
              <td><input className="lw-input money" readOnly value="—" style={{ textAlign: 'center', color: 'var(--lw-muted)' }} data-testid="lw-fin-carrier-fuel" /></td>
              <td>{numInput(load.fsc_amount, (e) => onChange('fsc_amount', e.target.value), 'lw-fin-customer-fuel', !canOverrideFsc)}</td>
              <td>
                {canOverrideFsc && (
                  <span style={{ display: 'inline-flex', gap: 4 }}>
                    {load.fsc_override && onRecalcFsc && (
                      <button style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--lw-muted)', padding: 2 }} onClick={onRecalcFsc} title="Recalculate from the customer's fuel table" data-testid="lw-fin-fuel-recalc"><RotateCcw size={13} /></button>
                    )}
                    <button style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--lw-red)', padding: 2 }} onClick={onRemoveFsc} title="Remove fuel surcharge from this load" data-testid="lw-fin-fuel-remove"><Trash2 size={13} /></button>
                  </span>
                )}
              </td>
            </tr>
          )}
          {!load.fsc_enabled && load.fsc_override && canOverrideFsc && onRecalcFsc && (
            <tr>
              <td colSpan={4} style={{ fontSize: 11, color: 'var(--lw-muted)' }}>
                Fuel surcharge removed manually ·{' '}
                <button className="lw-link" style={{ fontSize: 11 }} onClick={onRecalcFsc} data-testid="lw-fin-fuel-restore">Recalculate from customer table</button>
              </td>
            </tr>
          )}
          {accessorials.map((acc, idx) => (
            <tr key={acc._key || idx}>
              <td>
                <AccessorialDescInput
                  value={acc.description || ''}
                  onSelect={(label) => onUpdateAccessorial(idx, 'description', label)}
                  testId={`lw-acc-desc-${idx}`}
                />
              </td>
              <td>{numInput(acc.carrier_amount, (e) => onUpdateAccessorial(idx, 'carrier_amount', e.target.value), `lw-acc-carrier-${idx}`)}</td>
              <td>{numInput(acc.customer_amount, (e) => onUpdateAccessorial(idx, 'customer_amount', e.target.value), `lw-acc-customer-${idx}`)}</td>
              <td style={{ textAlign: 'center' }}>
                <button
                  onClick={() => onRemoveAccessorial(idx)}
                  title="Remove accessorial"
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--lw-red)', padding: 2 }}
                  data-testid={`lw-acc-delete-${idx}`}
                >
                  <Trash2 size={14} />
                </button>
              </td>
            </tr>
          ))}
          <tr style={{ borderTop: '2px solid var(--lw-border-strong)' }}>
            <td style={{ fontWeight: 700 }}>Total</td>
            <td style={{ textAlign: 'right', fontWeight: 700, fontSize: 14, paddingRight: 10, fontVariantNumeric: 'tabular-nums' }} data-testid="lw-fin-carrier-total">{money(carrierTotal)}</td>
            <td style={{ textAlign: 'right', fontWeight: 700, fontSize: 14, paddingRight: 10, fontVariantNumeric: 'tabular-nums' }} data-testid="lw-fin-customer-total">{money(customerTotal)}</td>
            <td></td>
          </tr>
        </tbody>
      </table>

      {/* Drop-off day calculations */}
      {(() => {
        const stops = load.stops || [];
        const dayFor = (s) => {
          if (!s?.drop_off || !s.trailer_dropped_date) return null;
          try {
            const end = s.trailer_ready_for_pickup_date ? new Date(s.trailer_ready_for_pickup_date) : new Date();
            return Math.max(0, Math.round((end - new Date(s.trailer_dropped_date)) / 86400000));
          } catch { return null; }
        };
        const rows = [];
        stops.forEach((s, i) => {
          const d = dayFor(s);
          if (d === null) return;
          let label = 'Days at Review';
          if (s.type === 'pickup') label = 'Days at Pickup';
          else if (i === stops.length - 1) label = 'Days at Delivery';
          rows.push({ label, days: d, ongoing: !s.trailer_ready_for_pickup_date });
        });
        if (!rows.length) return null;
        return (
          <div style={{ marginTop: 6, marginBottom: 6 }} data-testid="lw-drop-days-summary">
            {rows.map((r, i) => (
              <div key={r.label} className="lw-row" style={{ justifyContent: 'space-between', marginBottom: 2 }}>
                <span className="lw-label" style={{ fontSize: 12.5 }}>{r.label}</span>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: r.ongoing ? 'var(--lw-orange)' : 'var(--lw-text)' }} data-testid={`lw-days-${r.label.toLowerCase().replace(/\s/g, '-')}`}>
                  {r.days} {r.days === 1 ? 'day' : 'days'}{r.ongoing ? ' (ongoing)' : ''}
                </span>
              </div>
            ))}
          </div>
        );
      })()}

      <div className="lw-profitbox" data-testid="lw-profit-box">
        <span style={{ color: net >= 0 ? 'var(--lw-green)' : 'var(--lw-red)' }}>Net USD {money(net)}</span>
        <span>Profit {profitPct.toFixed(1)}%</span>
      </div>
    </div>
  );
};
